// EnrollData.jsx
import React from "react";
import { styled } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import HelpIcon from "@mui/icons-material/Help";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useNavigate } from "react-router-dom";

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: "8px",
  padding: "24px 28px",
  marginBottom: "20px",
  backgroundColor: "white",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  transition: "all 0.3s ease",
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  "&:hover": {
    backgroundColor: "#f8f9fa",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  color: "white",
  backgroundColor: "#041E42",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  borderRadius: "8px",
  padding: "12px 24px",
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "#062D4C",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: "14px 20px",
  },
}));

const HtmlTooltip = ({ title, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = React.useState(false);

  const handleTooltipOpen = () => {
    if (isMobile) {
      setOpen(true);
    }
  };

  const handleTooltipClose = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  const child = React.Children.toArray(children)[0];

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        title={title}
        open={isMobile ? open : undefined}
        onOpen={isMobile ? handleTooltipOpen : undefined}
        onClose={isMobile ? handleTooltipClose : undefined}
        disableHoverListener={isMobile}
        disableFocusListener={isMobile}
        disableTouchListener={false}
      >
        <span onClick={handleTooltipOpen}>{child}</span>
      </Tooltip>
    </ClickAwayListener>
  );
};

const EnrollData = ({
  approvedVendors,
  planTypes,
  employerName,
  plans,
  empState,
  reliusWeb,
  employerId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // We assume reliusWeb is guaranteed to be a valid string now
  function post(path, params, method = "post") {
    const form = document.createElement("form");
    form.method = method;
    form.action = path;
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        const hiddenField = document.createElement("input");
        hiddenField.type = "hidden";
        hiddenField.name = key;
        hiddenField.value = params[key];
        form.appendChild(hiddenField);
      }
    }
    document.body.appendChild(form);
    form.submit();
  }

  const handleClick = (pin) => {
    const formData = {
      LoginButton: "true",
      RANDOMNUM: "",
      PLANID: "",
      PIN: pin,
      DROP: "",
    };
    
    // Use external navigation with form data
    if (window.triggerReliusNavigation) {
      window.triggerReliusNavigation(reliusWeb, formData);
    } else {
      // Fallback to direct form submission if function not available
      const url = `https://${reliusWeb}/enrollinplan.aspx`;
      post(url, formData);
    }
  };

  const handleByPass = (planName) => {
    const matchingPlan = plans.find(
      (item) =>
        item.planUniqueId &&
        item.planUniqueId.split("_")[1] === planName.replace(/([() ])/g, "")
    );
    if (matchingPlan) {
      const passwordData =
        matchingPlan.planData &&
        matchingPlan.planData.find((data) => data.field === "planPassword");
      const pin = passwordData ? passwordData.value : "";
      handleClick(pin);
    }
  };

  const ButtonSelection = (planLabel) => (
    <StyledButton
      fullWidth={isMobile}
      disableElevation
      variant="contained"
      onClick={() => {
        // Check if this is an admin plan
        const isAdminPlan = planLabel === "403(b) Admin" || planLabel === "457(b) Admin";
        
        if (isAdminPlan) {
          // Check if there are vendors for this admin plan
          const hasVendors = approvedVendors && approvedVendors.some(vendor => {
            // Normalize both plan types for comparison
            const normalizedVendorPlanType = vendor.planType.replace(/\s/g, "").replace(/[()]/g, "").toLowerCase();
            const normalizedPlanLabel = planLabel.replace(/\s/g, "").replace(/[()]/g, "").toLowerCase();
            return normalizedVendorPlanType === normalizedPlanLabel;
          });
          
          if (hasVendors) {
            // Navigate to approved vendors page
            navigate(
              `/search-enroll/${employerId}/${empState}/approved-vendor/${encodeURIComponent(
                planLabel
              )}`,
              { state: { reliusWeb } }
            );
          } else {
            // No vendors for this admin plan, use enrollment logic
            handleByPass(planLabel);
          }
        } else {
          // Non-admin plan, use enrollment logic
          handleByPass(planLabel);
        }
      }}
      aria-label={`Enroll in ${planLabel}`}
    >
      Enroll
    </StyledButton>
  );

  const getPlanDescription = (planType) => {
    const matchingPlan = plans.find(
      (plan) => plan.code === planType || plan.label === planType
    );
    if (matchingPlan) {
      return matchingPlan.planDesc;
    }
    console.warn("No matching plan found for planType:", planType);
    return "Retirement plan information not found.";
  };

  if (!planTypes || planTypes.length === 0 || !employerName) return null;

  return (
    <Box sx={{ 
      mt: { xs: 2, md: 3 }, 
      px: isMobile ? 2 : 4, 
      py: 3,
      backgroundColor: "#fafafa",
      borderRadius: "12px",
      border: "1px solid #e0e0e0"
    }}>
      <Typography
        variant="h5"
        sx={{
          mb: isMobile ? 3 : 4,
          textAlign: "center",
          fontWeight: "bold",
          fontSize: { xs: "1.1rem", md: "1.5rem" },
          color: "#2c3e50"
        }}
      >
        Plans for {employerName}:
      </Typography>

      <List
        sx={{
          maxWidth: isMobile ? "100%" : 600,
          margin: "0 auto",
          px: isMobile ? 1 : 0,
        }}
      >
        {planTypes.map((item, index) => (
          <StyledListItem
            key={index}
            sx={{ 
              flexDirection: isMobile ? "column" : "row",
              marginBottom: "16px"
            }}
          >
            <ListItemText
              primary={
                <Box display="flex" alignItems="center">
                  <span>{`${item.label} Savings Plan`}</span>
                  <HtmlTooltip
                    title={<span>{getPlanDescription(item.label)}</span>}
                  >
                    <span>
                      <HelpIcon
                        color="primary"
                        sx={{
                          fontSize: "18px",
                          marginLeft: "16px",
                          cursor: "pointer",
                          opacity: 0.8,
                          transition: "opacity 0.2s ease",
                          "&:hover": {
                            opacity: 1,
                          }
                        }}
                        aria-label={`More information about ${item.label}`}
                      />
                    </span>
                  </HtmlTooltip>
                </Box>
              }
              sx={{
                textAlign: isMobile ? "center" : "left",
                mb: isMobile ? 1 : 0,
              }}
            />
            <ListItemSecondaryAction
              sx={{
                position: isMobile ? "relative" : "absolute",
                right: isMobile ? 0 : "16px",
                width: isMobile ? "100%" : "auto",
                textAlign: "right",
                mt: isMobile ? 1 : 0,
              }}
            >
              {ButtonSelection(item.label)}
            </ListItemSecondaryAction>
          </StyledListItem>
        ))}
      </List>
    </Box>
  );
};

export default EnrollData;
